import { CircularProgress } from '@material-ui/core';
import DraggableSelect from 'components/Form/Input/DraggableSelect/DraggableSelect';
import { PublishedStateEnum } from 'enums/publishedStateEnum';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { Field, Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { AppDispatch, AppState } from 'store';
import { setPagination } from 'store/content/content.actions';
import { IContentPagination, IFilterFieldsData, IFilterFormData } from 'store/content/content.interface';
import {
  getContentPaginationSelector,
  getContentTaxonomiesSelector,
  getFilterFieldsDataSelector,
} from 'store/content/content.selectors';
import { getContentSpinnerSelector } from 'store/spinner/spinner.selectors';
import { parseTaxonomies } from 'utils/formatters/parseTaxonomies';
import { KontentDeliveryTaxonomyDTO } from 'utils/restApplicationClient';
import {
  CustomFilterForm,
  CustomPaper,
  CustomPopper,
  FilterFormButton,
  FilterFormButtonContainer,
  PopperArrow,
} from './FilterStyles';

interface IProps {
  anchorEl?: HTMLElement;
  id: string;
  handleClose: () => void;
}

interface IState {
  filters: IFilterFormData;
}

interface IStateProps {
  contentSpinner: boolean;
  pagination: IContentPagination;
  fieldsData: IFilterFieldsData;
  taxonomies: Array<KontentDeliveryTaxonomyDTO>;
}

interface IDispatchProps {
  setPagination: (payload: IContentPagination) => AppDispatch;
}

type PropType = WithTranslation & IProps & IStateProps & IDispatchProps;

export class FilterPopper extends Component<PropType, IState> {
  private emptyFilters: IFilterFormData = {
    contentType: [],
    workflowStep: [],
    publishedState: [],
    taxonomies: [],
  };

  constructor(props: PropType) {
    super(props);
    const { contentType, workflowStep, publishedState, taxonomies } = props.pagination;
    const filters: IFilterFormData = {
      contentType: contentType.map((contentType) => ({ value: contentType, label: contentType.name })),
      workflowStep: workflowStep.map((workflowStep) => ({ value: workflowStep, label: workflowStep.name })),
      publishedState: publishedState.map((publishedState) => ({ value: publishedState, label: publishedState })),
      taxonomies,
    };

    this.state = {
      filters,
    };
  }

  componentDidUpdate({ pagination: previousPagination }: PropType): void {
    const { pagination } = this.props;

    if (previousPagination !== pagination) {
      const { contentType, workflowStep, publishedState, taxonomies } = pagination;
      const filters: IFilterFormData = {
        contentType: contentType.map((contentType) => ({ value: contentType, label: contentType.name })),
        workflowStep: workflowStep.map((workflowStep) => ({ value: workflowStep, label: workflowStep.name })),
        publishedState: publishedState.map((publishedState) => ({ value: publishedState, label: publishedState })),
        taxonomies,
      };

      this.setState({ filters });
    }
  }

  onSubmit = ({ contentType, workflowStep, publishedState, taxonomies }: IFilterFormData): void => {
    const { handleClose, setPagination, pagination } = this.props;

    setPagination({
      ...pagination,
      page: 0,
      contentType: contentType.map((contentType) => contentType.value),
      workflowStep: workflowStep.map((workflowStep) => workflowStep.value),
      publishedState: publishedState.map((publishedState) => publishedState.value),
      taxonomies,
    });
    handleClose();
  };

  handleResetClick = (form: FormApi<IFilterFormData, Partial<IFilterFormData>>) => (): void => {
    form.change('contentType', []);
    form.change('publishedState', []);
    form.change('workflowStep', []);
    form.change('taxonomies', []);
    form.submit();
  };

  render(): JSX.Element {
    const {
      t,
      anchorEl,
      id,
      contentSpinner,
      fieldsData: { workflowSteps, contentTypes },
      taxonomies,
    } = this.props;
    const { filters } = this.state;
    const parsedTaxonomies = parseTaxonomies(taxonomies, undefined, undefined);

    return (
      <CustomPopper
        id={id}
        open={!!anchorEl}
        anchorEl={anchorEl}
        disablePortal={true}
        placement="bottom-start"
        popperOptions={{
          modifiers: {
            offset: {
              offset: '0, 10',
            },
          },
        }}
      >
        <PopperArrow />
        <CustomPaper elevation={3}>
          <Form
            onSubmit={this.onSubmit}
            subscription={{ pristine: true, submitting: true, values: true }}
            initialValues={filters}
            render={({ handleSubmit, values, form }): JSX.Element => (
              <CustomFilterForm onSubmit={handleSubmit}>
                <Field
                  name="contentType"
                  component={DraggableSelect}
                  label="addcontent.contentType"
                  options={contentTypes.map((data) => ({ value: data, label: data.name }))}
                  noOptionsText={t('addcontent.noOptions')}
                  placeholder={t('common.selectPlaceholder')}
                  multi
                />
                <Field
                  name="workflowStep"
                  component={DraggableSelect}
                  label="addcontent.workflowStep"
                  options={workflowSteps.map((data) => ({ value: data, label: data.name }))}
                  noOptionsText={t('addcontent.noOptions')}
                  placeholder={t('common.selectPlaceholder')}
                  multi
                />
                <Field
                  name="publishedState"
                  component={DraggableSelect}
                  label="addcontent.publishedState"
                  options={[
                    { value: PublishedStateEnum.PUBLISHED, label: PublishedStateEnum.PUBLISHED },
                    { value: PublishedStateEnum.UNPUBLISHED, label: PublishedStateEnum.UNPUBLISHED },
                    { value: PublishedStateEnum.NOT_PUBLISHED_YET, label: PublishedStateEnum.NOT_PUBLISHED_YET },
                  ]}
                  noOptionsText={t('addcontent.noOptions')}
                  placeholder={t('common.selectPlaceholder')}
                  multi
                />
                <Field
                  name="taxonomies"
                  component={DraggableSelect}
                  label="addcontent.taxonomy"
                  options={parsedTaxonomies}
                  noOptionsText={t('addcontent.noOptions')}
                  placeholder={t('common.selectPlaceholder')}
                  multi
                  isTaxonomy
                />
                <FilterFormButtonContainer>
                  <FilterFormButton
                    size="medium"
                    variant="outlined"
                    color="primary"
                    onClick={this.handleResetClick(form)}
                  >
                    {t('common.reset')}
                  </FilterFormButton>
                  <FilterFormButton
                    type="submit"
                    size="medium"
                    variant="contained"
                    color="secondary"
                    disabled={contentSpinner || !values}
                  >
                    {contentSpinner ? <CircularProgress size="24px" color="secondary" /> : t('common.apply')}
                  </FilterFormButton>
                </FilterFormButtonContainer>
              </CustomFilterForm>
            )}
          />
        </CustomPaper>
      </CustomPopper>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  contentSpinner: getContentSpinnerSelector(state),
  pagination: getContentPaginationSelector(state),
  fieldsData: getFilterFieldsDataSelector(state),
  taxonomies: getContentTaxonomiesSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  setPagination: (payload: IContentPagination): AppDispatch => dispatch(setPagination(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(FilterPopper));
